<template>
  <Teleport to="body">
    <div v-if="modelValue" class="fixed inset-0 z-[70] overflow-y-auto">
      <div class="fixed inset-0 bg-black/50" @click="close"></div>
      <div class="relative flex min-h-screen items-center justify-center p-4">
        <div class="relative flex max-h-[92vh] w-full max-w-4xl flex-col overflow-hidden rounded-lg bg-white shadow-xl">
          <header class="flex items-start justify-between gap-4 border-b border-slate-200 px-6 py-4">
            <div>
              <h3 class="text-lg font-semibold text-slate-900">{{ modalTitle }}</h3>
              <p class="mt-1 text-sm text-slate-500">维护企营增客户主档信息，规划师由系统按创建人自动记录。</p>
            </div>
            <button
              type="button"
              class="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
              @click="close"
            >
              <span class="sr-only">关闭</span>
              <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </header>

          <main class="min-h-0 flex-1 overflow-y-auto px-6 py-5">
            <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
              <label class="block md:col-span-2">
                <span class="form-label">公司名称 <span class="text-red-500">*</span></span>
                <input
                  v-model="form.companyName"
                  type="text"
                  class="form-input mt-1 w-full"
                  placeholder="请输入公司名称"
                >
              </label>

              <label class="block">
                <span class="form-label">注册地 <span class="text-red-500">*</span></span>
                <el-cascader
                  ref="areaCascaderRef"
                  v-model="areaCodes"
                  :options="regionTree"
                  :props="{ checkStrictly: true }"
                  placeholder="请选择省市区"
                  filterable
                  clearable
                  class="mt-1 w-full"
                  @change="handleAreaChange"
                />
              </label>

              <label class="block">
                <span class="form-label">财务专员</span>
                <UserSelector
                  v-model="form.financeUserId"
                  class="mt-1"
                  :multiple="false"
                  placeholder="请选择财务专员"
                  @confirm="handleFinanceUserConfirm"
                  @update:modelValue="handleFinanceUserChange"
                />
              </label>

              <label class="block">
                <span class="form-label">渠道来源</span>
                <input
                  v-model="form.channelSource"
                  type="text"
                  maxlength="128"
                  class="form-input mt-1 w-full"
                  placeholder="请输入渠道来源"
                >
              </label>

              <label class="block">
                <span class="form-label">个税申报人数</span>
                <input
                  v-model.number="form.personalTaxDeclarationCount"
                  type="number"
                  min="0"
                  step="1"
                  class="form-input mt-1 w-full"
                  placeholder="请输入人数"
                >
              </label>

              <label class="block">
                <span class="form-label">上年实际业务规模（万元）</span>
                <input
                  v-model.number="form.lastYearActualBusinessScaleWan"
                  type="number"
                  min="0"
                  step="0.01"
                  class="form-input mt-1 w-full"
                  placeholder="单位：万元"
                >
              </label>

              <div class="grid grid-cols-1 gap-4 md:col-span-2 md:grid-cols-4">
                <label class="block">
                  <span class="form-label">是否有房产</span>
                  <el-select v-model="form.hasProperty" class="form-select mt-1 w-full">
                    <el-option label="未知" :value="null" />
                    <el-option label="是" :value="true" />
                    <el-option label="否" :value="false" />
                  </el-select>
                </label>

                <label class="block">
                  <span class="form-label">是否三章代管</span>
                  <el-select v-model="form.sealManaged" class="form-select mt-1 w-full">
                    <el-option label="未知" :value="null" />
                    <el-option label="是" :value="true" />
                    <el-option label="否" :value="false" />
                  </el-select>
                </label>

                <label class="block">
                  <span class="form-label">是否营业执照代管</span>
                  <el-select v-model="form.licenseManaged" class="form-select mt-1 w-full">
                    <el-option label="未知" :value="null" />
                    <el-option label="是" :value="true" />
                    <el-option label="否" :value="false" />
                  </el-select>
                </label>

                <label class="block">
                  <span class="form-label">是否实际经营</span>
                  <el-select v-model="form.actuallyOperating" class="form-select mt-1 w-full">
                    <el-option label="未知" :value="null" />
                    <el-option label="是" :value="true" />
                    <el-option label="否" :value="false" />
                  </el-select>
                </label>
              </div>

              <div class="md:col-span-2">
                <InvoiceCustomerTagSelect
                  v-model="form.tagIds"
                  :tags="availableTags"
                  :loading="tagsLoading"
                  :disabled="isSubmitting"
                />
              </div>

              <label class="block md:col-span-2">
                <span class="form-label">备注</span>
                <textarea
                  v-model="form.remark"
                  rows="3"
                  maxlength="1000"
                  class="form-textarea mt-1 w-full"
                  placeholder="请输入备注"
                ></textarea>
              </label>
            </div>

            <section v-if="!isEditMode" class="mt-5 rounded-lg border border-slate-200 bg-slate-50 p-4">
              <label class="inline-flex items-center gap-2 text-sm font-medium text-slate-700">
                <input v-model="form.joinMonth" type="checkbox" class="h-4 w-4 rounded border-slate-300 text-primary-600">
                同时加入当前月
              </label>

              <div v-if="form.joinMonth" class="mt-4 grid grid-cols-1 gap-4 md:grid-cols-3">
                <label class="block">
                  <span class="form-label">本月开票额度 <span class="text-red-500">*</span></span>
                  <input
                    v-model="form.targetOutputAmount"
                    type="number"
                    min="0"
                    step="100"
                    class="form-input mt-1 w-full bg-white"
                    placeholder="请输入额度"
                    @input="amountWarning = ''"
                  >
                  <p v-if="amountWarning" class="mt-1 text-xs text-amber-600">{{ amountWarning }}</p>
                </label>

                <label class="block">
                  <span class="form-label">本月利润率（%） <span class="text-red-500">*</span></span>
                  <input
                    v-model="form.targetProfitRatePercent"
                    type="number"
                    min="0"
                    step="0.01"
                    class="form-input mt-1 w-full bg-white"
                    placeholder="请输入利润率"
                  >
                </label>

                <label class="block">
                  <span class="form-label">名单区域</span>
                  <el-select
                    :model-value="form.regionBucketId ?? ''"
                    class="form-select mt-1 w-full bg-white"
                    @change="selectRegion"
                  >
                    <el-option label="按注册地自动归类" value="" />
                    <el-option
                      v-for="region in regions"
                      :key="region.id"
                      :label="region.regionName || '未命名区域'"
                      :value="region.id"
                    />
                  </el-select>
                </label>

                <div class="md:col-span-3">
                  <InvoiceProductQuotaEditor
                    v-model="form.outputProductQuotas"
                    title="销项品名组结构"
                    :target-amount="normalizedTargetAmount"
                    class="rounded-lg border border-slate-200 bg-white p-3"
                  />
                </div>

                <div class="md:col-span-3">
                  <InvoiceProductQuotaEditor
                    v-model="form.inputProductQuotas"
                    title="进项品名组结构"
                    :target-amount="normalizedTargetAmount"
                    class="rounded-lg border border-slate-200 bg-white p-3"
                  />
                </div>

                <div class="md:col-span-3">
                  <InvoiceCustomerInvoiceRulePanel
                    :customer="invoiceRuleCustomer"
                    :downstream-allocations="draftDownstreamAllocations"
                    :downstream-tag-preferences="draftDownstreamTagPreferences"
                    :disabled="isSubmitting"
                    @configure-downstream="openDraftDownstreamAllocation"
                    @configure-tag-preference="openDraftDownstreamTagPreference"
                  />
                </div>
              </div>
            </section>
          </main>

          <footer class="flex justify-end gap-3 border-t border-slate-200 px-6 py-4">
            <button type="button" class="btn-secondary" :disabled="isSubmitting" @click="close">取消</button>
            <button
              type="button"
              class="btn-primary"
              v-permission="isEditMode ? 'enterprise-invoice:customer:update' : 'enterprise-invoice:customer:create'"
              :disabled="isSubmitting"
              @click="submit"
            >
              {{ isSubmitting ? '保存中...' : '保存' }}
            </button>
          </footer>
        </div>
      </div>
    </div>
    <InvoiceDownstreamAllocationDialog
      v-model="downstreamAllocationDialog.visible"
      :customer="invoiceRuleCustomer"
      :customers="customers"
      :allocations="combinedDraftDownstreamAllocations"
      :submitting="isSubmitting"
      @submit="saveDraftDownstreamAllocations"
    />
    <InvoiceDownstreamTagPreferenceDialog
      v-model="downstreamTagPreferenceDialog.visible"
      :customer="invoiceRuleCustomer"
      :tags="availableTags"
      :preferences="draftDownstreamTagPreferences"
      :submitting="isSubmitting"
      @submit="saveDraftDownstreamTagPreferences"
    />
  </Teleport>
</template>

<script setup>
import { computed, reactive, ref, watch } from 'vue'
import { ElMessage } from 'element-plus'
import UserSelector from '@/components/UserSelector/index.vue'
import {
  buildChinaAreaOptions,
  closeChinaAreaCascaderAfterDistrict,
  codeTextMap
} from '@/utils/chinaAreaOptions.mjs'
import { formatInvoiceAmount, normalizeAmountInput, parsePercentInput } from '../utils'
import {
  defaultProductQuotas,
  normalizeProductQuotas,
  quotaTotalMatched,
  validateProductQuotaList
} from '../productQuota'
import { downstreamDraftSummary } from '../downstreamRules.mjs'
import InvoiceProductQuotaEditor from './InvoiceProductQuotaEditor.vue'
import InvoiceCustomerTagSelect from './InvoiceCustomerTagSelect.vue'
import InvoiceCustomerInvoiceRulePanel from './InvoiceCustomerInvoiceRulePanel.vue'
import InvoiceDownstreamAllocationDialog from './InvoiceDownstreamAllocationDialog.vue'
import InvoiceDownstreamTagPreferenceDialog from './InvoiceDownstreamTagPreferenceDialog.vue'

const DRAFT_PLAN_CUSTOMER_ID = '__new_customer__'

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  mode: { type: String, default: 'create' },
  customer: { type: Object, default: null },
  availableTags: { type: Array, default: () => [] },
  selectedTagIds: { type: Array, default: () => [] },
  tagsLoading: { type: Boolean, default: false },
  regions: { type: Array, default: () => [] },
  customers: { type: Array, default: () => [] },
  downstreamAllocations: { type: Array, default: () => [] },
  downstreamTagPreferences: { type: Array, default: () => [] },
  submitting: { type: Boolean, default: false }
})

const emit = defineEmits(['update:modelValue', 'submit'])

const form = reactive({
  companyName: '',
  registeredRegion: '',
  registeredRegionCodes: '',
  financeUserId: null,
  financeUserName: '',
  channelSource: '',
  personalTaxDeclarationCount: null,
  hasProperty: null,
  lastYearActualBusinessScaleWan: null,
  sealManaged: null,
  licenseManaged: null,
  actuallyOperating: null,
  tagIds: [],
  remark: '',
  joinMonth: false,
  targetOutputAmount: null,
  targetProfitRatePercent: '',
  regionBucketId: null,
    inputProductQuotas: defaultProductQuotas(0),
    outputProductQuotas: defaultProductQuotas(0)
})

const areaCodes = ref([])
const areaCascaderRef = ref(null)
const amountWarning = ref('')
const draftDownstreamAllocations = ref([])
const draftDownstreamTagPreferences = ref([])
const downstreamAllocationDialog = reactive({ visible: false })
const downstreamTagPreferenceDialog = reactive({ visible: false })

const isEditMode = computed(() => props.mode === 'edit')
const isSubmitting = computed(() => props.submitting)
const modalTitle = computed(() => isEditMode.value ? '编辑客户主档' : '新增客户主档')
const normalizedTargetAmount = computed(() => normalizeAmountInput(form.targetOutputAmount) || 0)
const regionTree = computed(() => buildChinaAreaOptions())
const combinedDraftDownstreamAllocations = computed(() => [
  ...(props.downstreamAllocations || []),
  ...draftDownstreamAllocations.value
])
const invoiceRuleCustomer = computed(() => ({
  id: DRAFT_PLAN_CUSTOMER_ID,
  planCustomerId: DRAFT_PLAN_CUSTOMER_ID,
  customerId: null,
  companyName: form.companyName,
  companyNameSnapshot: form.companyName,
  registeredRegion: form.registeredRegion,
  registeredRegionSnapshot: form.registeredRegion,
  regionBucketId: form.regionBucketId,
  targetOutputAmount: normalizedTargetAmount.value,
  targetProfitRate: parsePercentInput(form.targetProfitRatePercent)
}))

function resetForm() {
  const customer = props.customer || {}
  Object.assign(form, {
    companyName: customer.companyName || '',
    registeredRegion: customer.registeredRegion || '',
    registeredRegionCodes: customer.registeredRegionCodes || '',
    financeUserId: customer.financeUserId || null,
    financeUserName: customer.financeUserName || '',
    channelSource: customer.channelSource || '',
    personalTaxDeclarationCount: numberOrNull(customer.personalTaxDeclarationCount),
    hasProperty: booleanOrNull(customer.hasProperty),
    lastYearActualBusinessScaleWan: numberOrNull(customer.lastYearActualBusinessScaleWan),
    sealManaged: booleanOrNull(customer.sealManaged),
    licenseManaged: booleanOrNull(customer.licenseManaged),
    actuallyOperating: booleanOrNull(customer.actuallyOperating),
    tagIds: normalizeTagIds(props.selectedTagIds),
    remark: customer.remark || '',
    joinMonth: false,
    targetOutputAmount: null,
    targetProfitRatePercent: '',
    regionBucketId: null,
    inputProductQuotas: defaultProductQuotas(0),
    outputProductQuotas: defaultProductQuotas(0)
  })
  areaCodes.value = parseRegionCodes(form.registeredRegionCodes)
  amountWarning.value = ''
  draftDownstreamAllocations.value = []
  draftDownstreamTagPreferences.value = []
  downstreamAllocationDialog.visible = false
  downstreamTagPreferenceDialog.visible = false
}

function close() {
  emit('update:modelValue', false)
}

function numberOrNull(value) {
  if (value === null || value === undefined || value === '') return null
  const number = Number(value)
  return Number.isFinite(number) ? number : null
}

function booleanOrNull(value) {
  if (value === true || value === 'true' || value === 1 || value === '1') return true
  if (value === false || value === 'false' || value === 0 || value === '0') return false
  return null
}

function parseRegionCodes(value) {
  if (!value) return []
  return String(value).split(',').map(item => item.trim()).filter(Boolean)
}

function handleAreaChange(value) {
  const codes = Array.isArray(value) ? value : []
  areaCodes.value = codes
  form.registeredRegionCodes = codes.join(',')
  form.registeredRegion = codes.map(code => codeTextMap[code]).filter(Boolean).join('')
  closeChinaAreaCascaderAfterDistrict(areaCascaderRef.value, value)
}

function handleFinanceUserConfirm(users) {
  const user = Array.isArray(users) ? users[0] : users
  form.financeUserId = user?.id || null
  form.financeUserName = user?.name || user?.displayName || user?.username || ''
}

function handleFinanceUserChange(value) {
  form.financeUserId = value || null
  if (!value) {
    form.financeUserName = ''
  }
}

function selectRegion(value) {
  const region = props.regions.find(item => String(item.id) === String(value))
  form.regionBucketId = region ? region.id : null
}

function openDraftDownstreamAllocation() {
  if (!form.joinMonth) return
  downstreamAllocationDialog.visible = true
}

function openDraftDownstreamTagPreference() {
  if (!form.joinMonth) return
  downstreamTagPreferenceDialog.visible = true
}

function validateMaster() {
  if (!form.companyName.trim()) {
    ElMessage.warning('请填写公司名称')
    return false
  }
  if (!form.registeredRegion.trim()) {
    ElMessage.warning('请选择注册地')
    return false
  }
  if (form.personalTaxDeclarationCount !== null && Number(form.personalTaxDeclarationCount) < 0) {
    ElMessage.warning('个税申报人数不能为负数')
    return false
  }
  if (form.lastYearActualBusinessScaleWan !== null && Number(form.lastYearActualBusinessScaleWan) < 0) {
    ElMessage.warning('上年实际业务规模不能为负数')
    return false
  }
  return true
}

function validateMonth() {
  if (!form.joinMonth) return true
  const normalizedAmount = normalizeAmountInput(form.targetOutputAmount)
  const inputAmount = Number(String(form.targetOutputAmount ?? '').trim())
  if (!Number.isFinite(inputAmount) || normalizedAmount === null || normalizedAmount <= 0) {
    ElMessage.warning('请填写有效的本月开票额度')
    return false
  }
  if (inputAmount !== normalizedAmount) {
    const warning = '本月开票额度将按 100 元取整为 ' + formatInvoiceAmount(normalizedAmount)
    if (amountWarning.value !== warning) {
      amountWarning.value = warning
      form.targetOutputAmount = normalizedAmount
      ElMessage.warning(warning)
      return false
    }
  }
  const targetProfitRate = parsePercentInput(form.targetProfitRatePercent)
  if (!Number.isFinite(targetProfitRate) || targetProfitRate < 0) {
    ElMessage.warning('请填写有效的本月利润率')
    return false
  }
  if (!validateProductQuotas(form.inputProductQuotas, '进项品名组结构', normalizedAmount)) return false
  if (!validateProductQuotas(form.outputProductQuotas, '销项品名组结构', normalizedAmount)) return false
  if (!validateDraftDownstreamAllocations(normalizedAmount)) return false
  return true
}

function validateProductQuotas(value, label, targetAmount) {
  const errors = validateProductQuotaList(value, targetAmount)
  if (errors.length === 0 && quotaTotalMatched(value, targetAmount)) return true
  ElMessage.warning(errors[0] || label)
  return false
}

function nullableNumber(value) {
  const number = numberOrNull(value)
  return number === null ? null : number
}

function validateDraftDownstreamAllocations(targetAmount) {
  const summary = downstreamDraftSummary(draftDownstreamAllocations.value, {
    targetOutputAmount: targetAmount
  })
  if (!summary.overAllocated) return true
  ElMessage.warning('指定下游金额不能超过本月开票额度')
  return false
}

function targetCustomerName(planCustomerId) {
  const target = (props.customers || []).find(item => String(item.id ?? item.planCustomerId) === String(planCustomerId))
  return target?.companyNameSnapshot ||
    target?.companyName ||
    target?.customerName ||
    target?.name ||
    ''
}

function tagName(tagId) {
  const tag = (props.availableTags || []).find(item => String(item.id) === String(tagId))
  return tag?.tagName || tag?.name || ''
}

function saveDraftDownstreamAllocations(payload) {
  draftDownstreamAllocations.value = (payload?.allocations || []).map((item, index) => ({
    ...item,
    fromPlanCustomerId: DRAFT_PLAN_CUSTOMER_ID,
    toCompanyName: targetCustomerName(item.toPlanCustomerId),
    displayOrder: Number(item.displayOrder || index + 1)
  }))
  downstreamAllocationDialog.visible = false
}

function saveDraftDownstreamTagPreferences(payload) {
  draftDownstreamTagPreferences.value = (payload?.preferences || []).map((item, index) => ({
    ...item,
    fromPlanCustomerId: DRAFT_PLAN_CUSTOMER_ID,
    tagName: tagName(item.tagId),
    priority: Number(item.priority || index + 1)
  }))
  downstreamTagPreferenceDialog.visible = false
}

function submitDownstreamAllocations() {
  return draftDownstreamAllocations.value.map((item, index) => ({
    toPlanCustomerId: item.toPlanCustomerId,
    amount: Number(item.amount),
    enabled: item.enabled !== false,
    displayOrder: Number(item.displayOrder || index + 1),
    remark: item.remark || null
  }))
}

function submitDownstreamTagPreferences() {
  return draftDownstreamTagPreferences.value.map((item, index) => ({
    tagId: item.tagId,
    priority: Number(item.priority || index + 1),
    enabled: item.enabled !== false
  }))
}

function buildPayload() {
  const customer = {
    companyName: form.companyName.trim(),
    registeredRegion: form.registeredRegion.trim(),
    registeredRegionCodes: form.registeredRegionCodes || null,
    financeUserId: form.financeUserId || null,
    financeUserName: form.financeUserName || null,
    channelSource: form.channelSource?.trim() || null,
    personalTaxDeclarationCount: nullableNumber(form.personalTaxDeclarationCount),
    hasProperty: form.hasProperty,
    lastYearActualBusinessScaleWan: nullableNumber(form.lastYearActualBusinessScaleWan),
    sealManaged: form.sealManaged,
    licenseManaged: form.licenseManaged,
    actuallyOperating: form.actuallyOperating,
    remark: form.remark?.trim() || null
  }
  const targetOutputAmount = form.joinMonth ? normalizeAmountInput(form.targetOutputAmount) : null
  const month = {
    targetOutputAmount,
    targetProfitRate: form.joinMonth ? parsePercentInput(form.targetProfitRatePercent) : null,
    regionBucketId: form.joinMonth ? form.regionBucketId : null,
    inputProductQuotas: form.joinMonth ? normalizeProductQuotas(form.inputProductQuotas, targetOutputAmount) : null,
    outputProductQuotas: form.joinMonth ? normalizeProductQuotas(form.outputProductQuotas, targetOutputAmount) : null,
    downstreamAllocations: form.joinMonth ? submitDownstreamAllocations() : null,
    downstreamTagPreferences: form.joinMonth ? submitDownstreamTagPreferences() : null
  }
  return {
    customer,
    tagIds: normalizeTagIds(form.tagIds),
    joinMonth: !isEditMode.value && form.joinMonth,
    month
  }
}

function normalizeTagIds(value) {
  return Array.isArray(value)
    ? value.filter(item => item !== null && item !== undefined && item !== '')
    : []
}

async function submit() {
  if (isSubmitting.value) return
  if (!validateMaster() || !validateMonth()) return
  emit('submit', buildPayload())
}

watch(() => props.modelValue, value => {
  if (value) resetForm()
})

watch(() => props.customer, () => {
  if (props.modelValue) resetForm()
})

watch(() => props.selectedTagIds, value => {
  if (props.modelValue) {
    form.tagIds = normalizeTagIds(value)
  }
}, { deep: true })
</script>
